
import React from 'react';
import { ArrowLeft, AlertTriangle, Usb, Shield, Wifi, RefreshCw, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface TroubleshootingSectionProps {
  onBack: () => void;
}

const TroubleshootingSection = ({ onBack }: TroubleshootingSectionProps) => {
  const troubleshootingSteps = [
    {
      icon: Usb,
      title: "Check USB Connection",
      description: "Ensure your iPhone is properly connected",
      steps: [
        "Try a different USB cable",
        "Use a different USB port on your computer",
        "Make sure the cable supports data transfer (not just charging)",
        "Clean the Lightning/USB-C port on your iPhone"
      ]
    },
    {
      icon: Shield,
      title: "Trust This Computer",
      description: "Your iPhone needs to trust this computer",
      steps: [
        "Unlock your iPhone",
        "Look for 'Trust This Computer?' popup",
        "Tap 'Trust' and enter your passcode",
        "If no popup appears, disconnect and reconnect the cable"
      ]
    },
    {
      icon: Settings,
      title: "Check iPhone Settings",
      description: "Verify your iPhone settings are correct",
      steps: [
        "Go to Settings > General > Reset",
        "Tap 'Reset Location & Privacy'",
        "Reconnect to computer and trust again",
        "Ensure iPhone is unlocked during connection"
      ]
    },
    {
      icon: RefreshCw,
      title: "Restart Devices",
      description: "Sometimes a fresh start helps",
      steps: [
        "Disconnect your iPhone",
        "Restart your iPhone",
        "Restart this application",
        "Reconnect your iPhone and try again"
      ]
    }
  ];

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header with Back Button */}
      <div className="flex items-center p-6 border-b border-gray-200">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mr-3 p-2 h-auto"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Troubleshooting</h1>
          <p className="text-sm text-gray-600">Common iPhone connection issues and solutions</p>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto">
          {/* Warning Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-800 mb-1">Before You Begin</h3>
              <p className="text-sm text-amber-700">
                Make sure your iPhone is unlocked and you have the original or MFi-certified cable. 
                Try these solutions in order, testing the connection after each step.
              </p>
            </div>
          </div>

          {/* Troubleshooting Steps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {troubleshootingSteps.map((step, index) => (
              <Card key={index} className="h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <step.icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{step.title}</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2">
                    {step.steps.map((stepText, stepIndex) => (
                      <li key={stepIndex} className="flex items-start space-x-2 text-sm">
                        <span className="w-5 h-5 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-xs font-medium mt-0.5 flex-shrink-0">
                          {stepIndex + 1}
                        </span>
                        <span className="text-gray-700">{stepText}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Help */}
          <div className="mt-8 bg-gray-50 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Still Having Issues?</h3>
            <div className="space-y-2 text-sm text-gray-700">
              <p>• Make sure you're using iOS 12.0 or later</p>
              <p>• Check if your iPhone appears in iTunes/Finder when connected</p>
              <p>• Try connecting to a different computer to isolate the issue</p>
              <p>• Contact Apple Support if hardware issues persist</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TroubleshootingSection;
